import React from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/Logo.png";
import { ChevronDown } from "lucide-react";
import hasPermission from "@/hooks/hasPermission";

const LeftBar2 = ({ sidebarLinks = [] }) => {
  const [activeMenu, setActiveMenu] = React.useState(null);
  const location = useLocation();

  const clean = (p) => {
    if (!p) return "";
    let s = p.trim();
    if (s.endsWith("/all")) s = s.slice(0, -4);
    if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
    return s;
  };

  const current = clean(location?.pathname || "");

  const filteredLinks = React.useMemo(() => {
    return sidebarLinks
      .map((item) => {
        if (item.permission && !hasPermission(item.permission)) {
          return null;
        }
        if (item.children && item.children.length > 0) {
          const allowedChildren = item.children.filter((child) => {
            if (child.permission) {
              return hasPermission(child.permission);
            }
            return true;
          });
          if (allowedChildren.length === 0) {
            return null;
          }
          return { ...item, children: allowedChildren };
        }
        return item;
      })
      .filter(Boolean);
  }, [sidebarLinks]);

  // Flatten all navigable leaf entries to determine the best match
  const leafEntries = React.useMemo(() => {
    const list = [];
    filteredLinks.forEach((item, itemIdx) => {
      if (item?.children && item.children.length > 0) {
        item.children.forEach((child, childIdx) => {
          if (child?.path) {
            list.push({
              path: child.path,
              matchPaths: child.matchPaths || [],
              itemIdx,
              childIdx,
            });
          }
        });
      } else if (item?.path) {
        list.push({
          path: item.path,
          matchPaths: item.matchPaths || [],
          itemIdx,
          childIdx: null,
        });
      }
    });
    return list;
  }, [filteredLinks]);

  // Find the single best matching leaf link
  const activeWinner = React.useMemo(() => {
    if (!current) return null;

    let bestEntry = null;
    let highestScore = 0;

    for (const entry of leafEntries) {
      const cTarget = clean(entry.path);
      let score = 0;

      if (cTarget && current === cTarget) {
        // Exact match has highest priority
        score = 1000 + cTarget.length;
      } else if (Array.isArray(entry.matchPaths) && entry.matchPaths.length > 0) {
        for (const mp of entry.matchPaths) {
          const cmp = clean(mp);
          if (cmp && current === cmp) {
            score = Math.max(score, 900 + cmp.length);
          } else if (
            cmp &&
            cmp !== "/" &&
            (current.startsWith(cmp + "/") || current.startsWith(cmp + "-"))
          ) {
            score = Math.max(score, 500 + cmp.length);
          }
        }
      }

      if (!score && cTarget && cTarget !== "/") {
        if (
          current.startsWith(cTarget + "/") ||
          current.startsWith(cTarget + "-")
        ) {
          // Prefix match
          score = cTarget.length;
        }
      }

      if (score > highestScore) {
        highestScore = score;
        bestEntry = entry;
      }
    }

    return bestEntry;
  }, [leafEntries, current]);

  const isParentActive = (item, idx) => {
    if (!activeWinner) return false;
    const itemIdx =
      typeof idx === "number"
        ? idx
        : filteredLinks.findIndex(
            (l) => l === item || l?.title === item?.title
          );
    return activeWinner.itemIdx === itemIdx;
  };

  const isChildActive = (child, pIdx, cIdx) => {
    if (!activeWinner) return false;
    return (
      activeWinner.childIdx !== null &&
      activeWinner.itemIdx === pIdx &&
      activeWinner.childIdx === cIdx
    );
  };

  const shouldExpand = (item, itemIdx) => {
    if (activeMenu === item?.title) return true;
    if (isParentActive(item, itemIdx)) return true;
    return false;
  };

  const toggleSubMenu = (title) => {
    setActiveMenu(activeMenu === title ? null : title);
  };

  return (
    <div className="pl-4 pt-4 w-64 bg-cblack  border-r shrink-0 ">
      <div className=" pl-2">
        <Link to="/home">
          <img className=" w-[150px] " src={logo} alt="" />
        </Link>
      </div>
      <div className="h-full overflow-y-auto pb-20">
        {filteredLinks.map((item, itemIdx) => {
          const isActive = isParentActive(item, itemIdx);
          const expanded = shouldExpand(item, itemIdx);

          return (
            <div key={item?.title} className="w-auto flex flex-col my-2.5 mt-5">
              <div
                className={`flex items-center space-x-3 cursor-pointer text-color5 hover:text-cyellow p-2 rounded ${
                  isActive ? "text-cyellow" : ""
                }`}
                onClick={() => item?.children && toggleSubMenu(item?.title)}
              >
                <Link
                  to={item?.children ? "" : item?.path}
                  className={` w-full flex justify-between items-center ${
                    isActive ? "text-cyellow" : ""
                  } `}
                >
                  <div className="flex flex-shrink-0 gap-2 items-center">
                    <span className=" text-xl ">{item?.icon}</span>
                    {item?.title}
                  </div>
                  {item?.children && (
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        expanded ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Link>
              </div>
              {item?.children && expanded && (
                <div className=" w-max ml-14 space-y-2 relative ">
                  {item?.children.map((child, childIdx) => {
                    const childActive = isChildActive(child, itemIdx, childIdx);

                    return (
                      <Link
                        key={child?.title}
                        to={child?.path}
                        className={`block text-sm relative hover:text-cyellow ${
                          childActive ? "text-cyellow" : "text-gray-300"
                        }`}
                      >
                        {child?.title}
                        {childActive && (
                          <div className="h-2 w-2 bg-cyellow rounded-full absolute top-1.5 -left-[19px] z-10"></div>
                        )}
                      </Link>
                    );
                  })}
                  <div className="absolute h-full bg-neutral-700 -top-1.5 -left-4 w-[2px] "></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeftBar2;
